import DeleteIcon from '@mui/icons-material/Delete'
import EastIcon from '@mui/icons-material/East'
import {
  Alert,
  Button,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { FieldArray, Formik, Form as FormikForm, FormikHelpers } from 'formik'
import { useTranslation } from 'react-i18next'

import { useAuthContext } from '../auth/hooks/useAuthContext'
import { ShiftPreferenceForm, shiftPreferenceSchema } from '../schema/input'
import { ShiftPreferenceType, SnackBarAlertType } from '../types/interface'
import { createPreference, deletePreference } from '../utils/schedule-actions'
import SubmitButton from './forms/SubmitButtonWrapper'

dayjs.extend(utc)
dayjs.extend(timezone)

const ShiftPreference = ({
  alert,
  data,
  refetch,
  status,
}: {
  alert: (value: SnackBarAlertType) => void
  data: ShiftPreferenceType[]
  refetch: () => void
  status: string
}) => {
  const { userStatus } = useAuthContext() // Initial load user data

  dayjs.tz.setDefault(userStatus?.tz) // Sets default user timezone

  const { t } = useTranslation()
  const TODAY = dayjs().tz().format('YYYY-MM-DD')

  const resetValue = {
    days: [] as number[],
    end_time: dayjs().tz().endOf('day').toDate(),
    id: undefined,
    start_time: dayjs().tz().startOf('day').toDate(),
    status,
  }

  const initialValues = {
    timeframe: data.length
      ? data.map((item) => ({
          days: item.days,
          end_time: dayjs(`${TODAY}T${item.endTime}`).toDate(),
          id: Number(item.id),
          start_time: dayjs(`${TODAY}T${item.startTime}`).toDate(),
          status,
        }))
      : [resetValue],
  } as ShiftPreferenceForm

  const handleDelete = async (id: number) => {
    await deletePreference(id)
      .then(() => {
        alert({
          message: `${t('shift.alerts.delete.success')}`,
          open: true,
          type: 'success',
        })
        refetch()
      })
      .catch(() => {
        alert({
          message: `${t('shift.alerts.delete.error')}`,
          open: true,
          type: 'error',
        })
      })
  }

  const handleSubmit = async (
    values: ShiftPreferenceForm,
    { setSubmitting }: FormikHelpers<ShiftPreferenceForm>
  ) => {
    if (!values?.timeframe) {
      return
    }

    const payload = values.timeframe.map((time) => {
      const { id, ...rest } = time

      const startTime = dayjs(time.start_time)
      const endTime = dayjs(time.end_time)

      const formattedTime = {
        ...rest,
        days: time.days as number[],
        end_time: endTime.format('HH:mm:ss'),
        id: id === null ? undefined : id,
        start_time: startTime.format('HH:mm:ss'),
      }

      return formattedTime
    })

    await createPreference(payload)
      .then(() => {
        alert({
          message: `${t('shift.alerts.create.success')}`,
          open: true,
          type: 'success',
        })
        setSubmitting(false)
        refetch()
      })
      .catch(() => {
        alert({
          message: `${t('shift.alerts.create.error')}`,
          open: true,
          type: 'error',
        })
        setSubmitting(false)
      })
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={shiftPreferenceSchema}
    >
      {(props) => (
        <FormikForm>
          <FieldArray name="timeframe">
            {({ push }) => (
              <Stack spacing={4}>
                {props.values.timeframe?.map((time, idx: number) => (
                  <Stack key={idx} spacing={2}>
                    {idx === 0 && (
                      <Alert severity="info"> {t('timing.choose_day')} </Alert>
                    )}
                    <ToggleButtonGroup
                      color="primary"
                      fullWidth
                      onChange={(_, value) => {
                        props.setFieldValue(`timeframe.${idx}.days`, value)
                      }}
                      value={time.days}
                    >
                      {[...Array(7)].map((_, dayIndex) => (
                        <ToggleButton key={dayIndex} value={dayIndex}>
                          {dayjs().day(dayIndex).format('ddd')}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <Stack alignItems="center" direction="row" spacing={1}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          label="Start Time"
                          onChange={(value) =>
                            props.setFieldValue(
                              `timeframe.${idx}.start_time`,
                              value
                            )
                          }
                          value={dayjs(time.start_time)}
                        />
                      </LocalizationProvider>
                      <EastIcon />
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          label="End Time"
                          onChange={(value) =>
                            props.setFieldValue(
                              `timeframe.${idx}.end_time`,
                              value
                            )
                          }
                          value={dayjs(time.end_time)}
                        />
                      </LocalizationProvider>
                      {!!idx && (
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(Number(time.id))}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                ))}
                <Button
                  onClick={() => push(resetValue)}
                  size="small"
                  variant="outlined"
                >
                  {t('timing.add_timeframe')}
                </Button>
                <SubmitButton disableElevation variant="contained">
                  {t('general.save')}
                </SubmitButton>
              </Stack>
            )}
          </FieldArray>
        </FormikForm>
      )}
    </Formik>
  )
}

export default ShiftPreference
